<template>
    <div :class="isLoading ? 'is-loading' : ''">

        <!-- filters, descriptions and links -->
        <aside class="container">

            <!-- section filter -->
            <ul class="nav nav-pills flex-nowrap flex-md-wrap gap-3 overflow-auto overflow-md-visible pt-2 pb-3 mb-4 px-2 px-md-0 d-print-none">
                <li class="nav-item" v-for="section in sections" :key="section.id">
                    <button :class="['nav-link shadow fs-7 px-4 py-2 text-nowrap', filters.section === section.id ? 'active' : '']" @click="selectSection(section.id)">{{ section.title }}</button>
                </li>
            </ul>

            <!-- description -->
            <div v-if="getCurrentSection?.description" v-html="getCurrentSection?.description" class="editor text-secondary fst-italic fs-6 mb-4"></div>

            <!-- links -->
            <div v-if="getCurrentSection?.authorsDirectionsUrl || getCurrentSection?.detailedInfoUrl" 
                class="bg-tertiary d-flex flex-column flex-md-row align-items-center justify-content-center mb-4 d-print-none"
            >
                <a v-if="getCurrentSection?.authorsDirectionsUrl" 
                    :href="getCurrentSection.authorsDirectionsUrl" 
                    class="link-secondary d-inline-block py-3 px-4"
                >
                    <strong>
                        <u>
                            {{ translate('Publications Authors Directions') }}
                        </u>
                    </strong>
                </a>
                <a v-if="getCurrentSection?.detailedInfoUrl" 
                    :href="getCurrentSection.detailedInfoUrl" 
                    class="link-secondary d-inline-block py-3 px-4"
                >
                    <strong>
                        <u>
                            {{ translate('Publications Detailed Info') }}
                        </u>
                    </strong>
                </a>
            </div>

            <!-- filters -->
            <div class="row align-items-center my-5 d-print-none" v-if="getCurrentSection?.filters?.showFilters">

                <!-- years range -->
                <div class="col-lg-4 mb-5 mb-lg-0" v-if="getCurrentSection.filters.years?.length">
                    <label class="small fw-semibold text-secondary mb-3">{{ translate('Choose range accroding to years') }}:</label>
                    <div class="d-flex align-items-center">
                        <span class="slider-marker pe-2">
                            {{ getCurrentSection.filters.years[0] }}
                        </span>
                        <div class="flex-grow-1 px-3">
                            <Slider
                                v-model="filters.years"
                                tooltipPosition="bottom"
                                :min="getCurrentSection.filters.years[0]"
                                :max="getCurrentSection.filters.years[1]"
                            ></Slider>
                        </div>
                        <span class="slider-marker ps-2">
                            {{ getCurrentSection.filters.years[1] }}
                        </span>
                    </div>
                </div>

                <!-- authors -->
                <div class="col-lg-4 mb-3 mb-lg-0" v-if="getCurrentSection.filters.authors?.length">
                    <vSelect  
                        style="width: 380px; max-width: 100%;"
                        class="TertiaryBg mx-auto"
                        :options="getCurrentSection.filters.authors" 
                        v-model="filters.author"  
                        :clearable="true"
                        :searchable="true"
                        :reduce="option => option.id" 
                        label="name"
                        :placeholder="translate('By authors name')"
                    >
                        <template #no-options="{ search, searching }">
                            <!-- no results -->
                            <p class="small" v-if="searching">
                                {{ translate('No items available') }}
                            </p>
                        </template>
                    </vSelect>
                </div>

                <!-- Article types -->
                <div class="col-lg-4" v-if="getCurrentSection.filters.articleTypes?.length">
                    <vSelect  
                        style="width: 380px; max-width: 100%;"
                        class="TertiaryBg mx-auto"
                        :options="getCurrentSection.filters.articleTypes" 
                        v-model="filters.types"  
                        :clearable="true"
                        :searchable="true"
                        :reduce="option => option.id" 
                        label="title"
                        multiple
                        :placeholder="translate('By articles type')"
                    >
                        <template #no-options="{ search, searching }">
                            <!-- no results -->
                            <p class="small" v-if="searching">
                                {{ translate('No items available') }}
                            </p>
                        </template>
                    </vSelect>
                </div>

            </div>

        </aside>
       
        <!-- list -->
        <div data-pagination-change-scroll-to>

            <div class="container">
                <p v-if="!displayedItems.length && !isLoading" class="text-center mb-4">
                    {{ translate('No items available') }}
                </p>
            </div>

            <article v-for="article in displayedItems" :key="article.id" :class="[article.isNew ? 'bg-tertiary' : '', 'py-4']">
                <div class="position-relative container">
                    <a :href="article.link" class="stretched-link"></a>
                    <div class="fs-6 fw-semibold text-secondary mb-2" v-if="article.isNew">
                        {{ translate('New Publication') }}
                    </div>
                    <div class="d-flex">
                        <div class="rounded-1 flex-shrink-0">
                            <img :src="getArticleImage(article.image)" :alt="article.title" class="img-fluid border border-1 shadow-sm">
                        </div>
                        <div class="min-w-0 flex-grow-1 ps-4 d-flex flex-column">
                            <p class="text-secondary fw-semibold">
                                <small class="text-nowrap">
                                    {{ article.date }}
                                </small>
                            </p>
                            <h2 class="text-truncate h6 my-2">
                                {{ article.title }}
                            </h2>
                            <p class="text-truncate-lines-5 mb-3">
                                {{ article.perex }}
                            </p>
                            <div class="hr-middle-after text-end mt-auto">
                                <span :class="[article.isNew ? 'bg-tertiary' : 'bg-white', 'text-secondary ps-5 pe-0 text-uppercase fw-semibold fs-3']">{{ translate('More') }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

        </div>

        <div class="mt-5 d-print-none">
            <div class="container">
                <Pagination 
                    :items-count="filteredItems.length"
                    v-model:current-page="pagination.currentPage" 
                    v-model:per-page="pagination.perPage"
                />
            </div>
        </div>

    </div>
</template>

<script>
import vSelect from 'vue-select';
import { paginate } from '@/scripts/helpers/pagination';
import { translate } from '@/scripts/mixins/translations';
import Pagination from '@/components/pagination/Pagination.vue';
import { watchFilters } from '@/scripts/mixins/filters';
import Slider from '@vueform/slider';

export default {
    components: {
        Pagination,
        vSelect,
        Slider
    },
    mixins: [
        watchFilters(),
        translate
    ],
    data(){
        return {
            isLoading: false,
            filters: {
                section: Number(new URL(window.location).searchParams.get('section')) || '',
                author: Number(new URL(window.location).searchParams.get('author')) || '',
                types: !!new URL(window.location).searchParams.get('types') ? 
                        new URL(window.location).searchParams.get('types').split(',').map(type => Number(type)) : [],
                years: !!new URL(window.location).searchParams.get('years') ? 
                        new URL(window.location).searchParams.get('years').split(',').map(type => Number(type)) : [0, 3000],
            },
            pagination: {
                currentPage: 1,
                perPage: parseInt(localStorage.getItem('list-per-page')) || 10
            },
            sections: [],
            publications: []
        }
    },
    methods: {
        selectSection(id){
            // switch section
            this.filters.section = id;

            // reset depending filters
            this.filters.author = '';
            this.filters.types = [];
            this.filters.years = [
                this.getCurrentSection?.filters?.years[0] || 0,
                this.getCurrentSection?.filters?.years[1] || 3000,
            ];
        },
        getArticleImage(url){
            if ( url.length )
            {
                return url;
            }

            return '/client-assets/public/images/tc-placeholder.svg';
        },
        loadPublications(){
            this.isLoading = true;

            axios.get(`publications/list`)
            .then(response => {
                const { data } = response;

                this.sections = data.sections;
                this.publications = data.publications;
            })
            .finally(() => {
                this.isLoading = false;
            });
        }
    },
    computed: {
        getCurrentSection(){

            return this.sections.find(section => {
                return section.id === this.filters.section;
            });

        },
        filteredItems(){
            let list = this.publications;

            // filter by section
            if ( this.filters.section ) 
            {
                list = list.filter(article => {
                    return article.section === this.filters.section;
                });
            }

            // apply other filters? Not every section has other filters available
            if ( !this.getCurrentSection?.filters?.showFilters )
            {
                return list;
            }

            // filter by year
            list = list.filter(article => {

                let FilterValue = this.filters.years;

                let year = parseInt(article.year);
                let minYear = parseInt(FilterValue[0]);
                let maxYear = parseInt(FilterValue[1]);

                return year >= minYear && year <= maxYear;
            });

            // filter by author
            if ( this.filters.author )
            {
                list = list.filter(article => {
                    return article.authors.includes( parseInt(this.filters.author) );
                });
            }

            // filter by article types
            if ( this.filters.types.length )
            {
                list = list.filter(article => {
                    let FilterValue = this.filters.types;

                    return article.articleTypes.some(type => FilterValue.includes(type));
                });
            }

            return list;
        },
        displayedItems(){
            return paginate(this.filteredItems, this.pagination.currentPage, this.pagination.perPage);
        }
    },
    watch: {
        'filters.author': function(newValue, oldValue) {
            // transform null value (after clearing author filter)
            // into empty string for url encoding, so refreshing the page won't break the filter
            if ( newValue === null ){
                this.filters.author = '';
            }
        }
    },
    mounted(){
        this.loadPublications();
    }
}
</script>